import { useState, useEffect, useRef, Fragment } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Transition } from '@headlessui/react';
import {
  PlusCircleIcon,
  FolderIcon,
  AcademicCapIcon,
  ArrowRightOnRectangleIcon,
  BookOpenIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { Mixpanel } from '../../services/Mixpanel';
import { useSidebarVisibility } from '../../contexts/SidebarVisibilityContext';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { useDiffitUserContext } from '../../contexts/DiffitUserContext';
import { WithTooltip } from '../../components/WithTooltip';
import { ProfileIconMenu } from '../../components/ProfileIconMenu';
import Image from '../../components/Image';
import { useSignInModal } from '../../contexts/SignInModalContext';
import { signInSourceForPath } from '../../utils/signInCopy';
import SidebarItem from './components/SidebarItem';
import SupportMenu from './components/SupportMenu';
import { SidebarSlot } from './components/SidebarSlot';
import { ProjectQuotaMeter } from './components/ProjectQuotaMeter';
import { RecentResourcesSidebar } from '../Shared/RecentResourcesSidebar';

const EXPANDED_WIDTH = 'w-[260px]'; // 260px
const COLLAPSED_WIDTH = 'w-[68px]'; // 68px

function SidebarIcon({ className }) {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      fill='none'
      viewBox='0 0 24 24'
      strokeWidth={1.5}
      stroke='currentColor'
      className={className}>
      <rect x='3' y='3' width='18' height='18' rx='2' />
      <line x1='9' y1='3' x2='9' y2='21' />
    </svg>
  );
}

function ProfileSection({ isExpanded }) {
  const { user } = useUser();

  if (!user) return null;

  return (
    <div className={`flex items-center ${!isExpanded ? 'mb-1' : ''}`}>
      <ProfileIconMenu sidebarExpanded={isExpanded} />
    </div>
  );
}

function SignedOutBottomSection({ isExpanded }) {
  // The sidebar is on screen on packet/project routes too, so signing in from
  // here lands a visitor in the same copy experiment as the packet overlay does.
  const { pathname } = useLocation();
  const showSignInModal = useSignInModal();

  return (
    <>
      <SidebarItem
        icon={EnvelopeIcon}
        label='Contact Us'
        href='https://support.diffit.me/hc/en-us/requests/new'
        external
        isExpanded={isExpanded}
      />
      <SidebarItem
        icon={ArrowRightOnRectangleIcon}
        label='Sign In'
        onClick={() => {
          Mixpanel.track('Log In Button', { location: 'Sidebar' });
          showSignInModal({ source: signInSourceForPath(pathname) });
        }}
        isExpanded={isExpanded}
      />
    </>
  );
}

export default function AppSidebar({ forceExpanded = false, onNavItemClick }) {
  const location = useLocation();
  const { isSignedIn, isLoaded } = useAuth();
  const { breakpoint } = useBreakpoints();
  // Courses proof of concept: only for users the diffit-courses flag lets in. Waits for
  // identify(), so the anonymous pre-identify evaluation never flashes the link.
  const { diffitCourses } = useFlags();
  const { isLDidentified } = useDiffitUserContext();

  // Determine storage context based on route
  const isPacketOrProject =
    location.pathname.includes('/p/') ||
    location.pathname.includes('/project/') ||
    // Courses proof of concept: a chat workspace, so it starts with the nav as a rail too.
    location.pathname.startsWith('/courses');

  // Home pages: sidebar preference lives in SidebarVisibilityContext (shared so the
  // collapsed-state logo can render inside the homepage scroll container).
  const { homeSidebarExpanded: homePreference, setHomeSidebarExpanded: setHomePreference } =
    useSidebarVisibility();
  // Packet/project pages: always start closed, no persistence
  const [packetPreference, setPacketPreference] = useState(false);

  // Force sidebar closed when navigating to any packet/project page
  useEffect(() => {
    if (isPacketOrProject) {
      setPacketPreference(false);
    }
  }, [location.pathname]);

  let userPreference, setUserPreference;
  if (isPacketOrProject) {
    userPreference = packetPreference;
    setUserPreference = setPacketPreference;
  } else {
    userPreference = homePreference;
    setUserPreference = setHomePreference;
  }
  const isLargeEnoughForInline = ['lg', 'xl', '2xl'].includes(breakpoint);
  const isMobile = ['xs', 'sm'].includes(breakpoint);

  // Auto-collapse when viewport shrinks below md
  const prevBreakpointRef = useRef(breakpoint);
  useEffect(() => {
    const wasMdOrAbove = ['md', 'lg', 'xl', '2xl'].includes(prevBreakpointRef.current);
    if (wasMdOrAbove && isMobile && userPreference) {
      setUserPreference(false);
    }
    prevBreakpointRef.current = breakpoint;
  }, [breakpoint]);

  // forceExpanded is used by the mobile drawer
  const isExpanded = forceExpanded || userPreference;
  const isInline = isExpanded && isLargeEnoughForInline;
  const isOverlay = isExpanded && !isLargeEnoughForInline && !forceExpanded;

  const toggleSidebar = () => {
    Mixpanel.track('Sidebar toggle', { action: userPreference ? 'close' : 'open' });
    setUserPreference(!userPreference);
  };

  const handleNavClick = (callback) => (e) => {
    if (callback) callback(e);
    // Close overlay on nav item click
    if (isOverlay) {
      setUserPreference(false);
    }
    if (onNavItemClick) onNavItemClick();
  };

  const sidebarContent = (
    <nav
      data-allow-click-when-logged-out
      className={`h-full max-h-screen flex flex-col border-r border-gray-200 print:hidden bg-gray-100 ${
        forceExpanded || isExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH
      } transition-all duration-200`}>
      {/* Top row — always present, matches resource header height (80px) */}
      <div
        className={`flex items-center px-3 h-[72px] pt-[18px] flex-shrink-0 ${isExpanded || forceExpanded ? 'justify-between' : 'justify-center'}`}>
        <div
          className={`flex items-center transition-opacity duration-300 ${
            isExpanded ? 'opacity-100' : 'opacity-0 w-0 pointer-events-none'
          }`}>
          <a
            href='/'
            className='flex flex-col justify-center'
            onClick={() => Mixpanel.track('Header click', { item: 'Logo' })}>
            <Image className='h-12 w-auto ml-[9px]' src='/logo.svg' alt='Diffit' />
          </a>
        </div>
        <WithTooltip
          key={isExpanded ? 'hide-sidebar' : 'show-sidebar'}
          text={isExpanded ? 'Hide sidebar' : 'Show sidebar'}
          position={isExpanded ? 'bottom' : 'right'}
          anchor='center'
          showArrow
          usePortal
          zIndex={55}>
          <button
            onClick={toggleSidebar}
            aria-label={isExpanded ? 'Hide sidebar' : 'Show sidebar'}
            className={`rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-200 transition-colors flex-shrink-0 p-2`}>
            {!forceExpanded && <SidebarIcon className='h-5 w-5' />}
          </button>
        </WithTooltip>
      </div>

      {/* Main nav items */}
      <div
        className={`flex flex-col gap-0.5 px-4 pt-6 pb-2 ${isExpanded ? 'flex-shrink-0' : 'flex-1'}`}>
        {isSignedIn && isLoaded ? (
          <>
            <SidebarItem
              icon={PlusCircleIcon}
              label='Create New'
              to='/'
              onClick={handleNavClick(() => {
                document
                  .getElementById('unlimited-scroll-container')
                  ?.scrollTo({ top: 0, behavior: 'smooth' });
              })}
              isExpanded={isExpanded}
            />
            {/* rounded-lg so the tour's green highlight ring matches the
                SidebarItem's own rounded-lg hover state */}
            <div data-tutorial-id='bts-my-resources-nav' className='rounded-lg'>
              <SidebarItem
                icon={FolderIcon}
                label='My Resources'
                to='/resources'
                onClick={handleNavClick()}
                isExpanded={isExpanded}
              />
            </div>
            {isLDidentified && diffitCourses && (
              <SidebarItem
                icon={AcademicCapIcon}
                label='Contexts'
                to='/courses'
                onClick={handleNavClick()}
                isExpanded={isExpanded}
              />
            )}
          </>
        ) : (
          <SidebarItem
            icon={PlusCircleIcon}
            label='Start New'
            to='/'
            onClick={handleNavClick(() => {
              document
                .getElementById('unlimited-scroll-container')
                ?.scrollTo({ top: 0, behavior: 'smooth' });
            })}
            isExpanded={isExpanded}
          />
        )}

        {isSignedIn && isLoaded ? (
          <SupportMenu isExpanded={isExpanded} />
        ) : (
          <SidebarItem
            icon={BookOpenIcon}
            label='Learn More'
            href='https://web.diffit.me'
            external
            isExpanded={isExpanded}
          />
        )}
      </div>

      {/* Portal slot for page-specific sidebar content (e.g. templates) */}
      <div className={`flex-1 px-2 ${isExpanded ? 'overflow-y-auto' : 'hidden'}`}>
        <div id='app-sidebar-page-content' />

        <RecentResourcesSidebar />
      </div>

      {/* `min-h-0` rather than `flex-shrink-0`: the card is the only thing here
          whose height is open-ended, and the profile row below it is how a
          teacher reaches their account. Letting this container give — and
          scrolling the card inside it — keeps that row on screen no matter how
          tall a card gets. Shrinking only happens once the nav overflows, so
          the ordinary layout is untouched. */}
      <div className='flex flex-col gap-1 px-4 pt-3 pb-[22px] min-h-0'>
        {/* One card, chosen by priority. SidebarSlot owns the whole decision —
            which card wins, the collapsed-rail and expand-transition rules, and
            holding the slot blank until every contender has resolved. See
            useSidebarCardLadder for the order.

            The scroll wrapper is the slot's height budget: a card taller than
            the space left over scrolls rather than pushing the profile row off
            the bottom of the viewport. A card that renders its own tooltip in
            here needs WithTooltip's `usePortal` to escape this container. */}
        <div className='min-h-0 overflow-y-auto'>
          {/* The desktop copy of this nav stays mounted below md (AppNavigation
              hides it with CSS, not a mount guard), so tell the slot whether
              this instance is one a teacher can actually see. */}
          <SidebarSlot isExpanded={isExpanded} isVisibleSurface={forceExpanded || !isMobile} />
        </div>

        {/* Outside the scroll wrapper so a tall ad scrolls instead of pushing the meter off. */}
        {isSignedIn && isLoaded && <ProjectQuotaMeter isExpanded={isExpanded} />}

        {isSignedIn && isLoaded ? (
          <ProfileSection isExpanded={isExpanded} />
        ) : (
          <SignedOutBottomSection isExpanded={isExpanded} />
        )}
      </div>
    </nav>
  );

  // When used as mobile drawer content (forceExpanded), just return the nav
  if (forceExpanded) {
    return sidebarContent;
  }

  return (
    <>
      {/* The sidebar itself - inline when collapsed or expanded+large, overlay when expanded+small */}
      <div
        className={`flex-shrink-0 h-full ${isOverlay ? 'relative' : ''}`}
        style={{ width: isInline ? '260px' : '68px', transition: 'width 200ms' }}>
        <div
          className={`h-full ${
            isOverlay ? 'fixed top-0 left-0 z-50' : 'relative z-40'
          } transition-all duration-200`}>
          {sidebarContent}
        </div>
      </div>

      {/* Overlay backdrop */}
      <Transition
        as={Fragment}
        show={isOverlay}
        enter='transition-opacity duration-300'
        enterFrom='opacity-0'
        enterTo='opacity-100'
        leave='transition-opacity duration-150'
        leaveFrom='opacity-100'
        leaveTo='opacity-0'>
        <div
          className='fixed inset-0 bg-black/20 z-40'
          onClick={() => setUserPreference(false)}
          aria-hidden='true'
          data-allow-click-when-logged-out
        />
      </Transition>
    </>
  );
}
